// ---------------------------------------------------------------------------
// What a film decision actually is
// ---------------------------------------------------------------------------
//
// Doug: "a one-off screening tonight outranks an established film with a long
// run". True, and `best` sorting on score alone is why — the film lane's
// scores cluster hard. Of the 26 films in the lane today, ten share 0.55 and
// six share 0.60, so the order inside the cluster is whatever the database
// returned. A single 22:00 screening tonight landed above a film playing 17
// times at four cinemas.
//
// The missing idea is that **a score says how good a film is and nothing
// about whether you can go**. Those are different questions and the feed only
// ever asked the first one.
//
// Two shapes, decided differently:
//
// **A run.** On for several days, often at several cinemas. You choose when,
// so it is a decision you can act on now or on Thursday. The more days and
// the more cinemas, the more likely it survives contact with a diary — which
// is worth something real, because a recommendation you cannot act on is not
// a recommendation. Days matter more than cinemas (0.6 to 0.4): another
// evening is a new chance to go, another cinema is the same evening somewhere
// slightly nearer.
//
// **A one-off.** One date, take it or miss it. Doug's steer is that it should
// "surface a few days ahead, not the same day", which is exactly right and
// also the opposite of what a date sort does. Tonight it is not a decision:
// he already has the evening, or it is 22:00. Three days out it is the most
// actionable thing in the lane. Two weeks out it is not a decision either,
// because he decides the week of.
//
// The adjustment is a band of about ±0.1 around the score, deliberately
// narrower than the spread of the lane. Wings of Desire at 0.92 still beats
// everything; what moves is the order inside a cluster of equals, which is
// the only place the old sort had nothing to say.

/** Whole days from one ISO date to another. Inlined rather than imported
 *  from `calendar` so this module has no dependencies at all and the test
 *  suite can load it without a resolver — see the note in entries.ts. Noon
 *  UTC on both sides, so a daylight-saving boundary cannot round a day away. */
const dayDiff = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000)

/** The day a showing falls on in `tz` (C2.1: the row's own zone, which the
 *  caller passes), inlined for the same reason — `zone.ts`'s `dayIn`. Not
 *  `slice(0, 10)`, the UTC day: a 00:30 Berlin screening is stored as 22:30
 *  the evening before in summer, and would count as a day it is not on
 *  (a3-02). */
const dayIn = (at: string, tz: string) => new Date(at).toLocaleDateString('en-CA', { timeZone: tz })

/** How many ways there are still to see this, and how soon the first one is. */
export interface RunShape {
  /** Distinct calendar dates still to come. */
  days: number
  /** Distinct buildings still showing it. */
  cinemas: number
  /** Days from today to the first remaining showing. */
  lead: number
}

export function runShape(
  showings: { starts_at: string | null; venue_id?: string | null; building_id?: string | null }[],
  today: string,
  /** The row's zone, which its showings' days are read in (C2.1). */
  tz: string,
  /** Injectable so a test can reason about a fixed evening. */
  now = new Date().toISOString(),
): RunShape {
  const ahead = showings.filter(s => s.starts_at && s.starts_at >= now)
  const dates = new Set(ahead.map(s => dayIn(s.starts_at!, tz)))
  const places = new Set(ahead.map(s => s.building_id ?? s.venue_id).filter(Boolean) as string[])
  const first = [...dates].sort()[0]
  return {
    days: dates.size,
    cinemas: places.size,
    lead: first ? dayDiff(today, first) : 0,
  }
}

/** The one-off curve, in days of notice.
 *
 *  Flat through the week Doug actually plans in, and penalised at both ends:
 *  on the day there is nothing to decide, and a fortnight out a single night
 *  is not yet a decision either. */
function oneOffAdjustment(lead: number): number {
  if (lead <= 0) return -0.12   // tonight
  if (lead === 1) return -0.06  // tomorrow: usually already spoken for
  if (lead <= 7) return 0       // the week he decides in
  return -0.03                  // too far out to hold one evening
}

/** Where a film should sit, given how good it is and how gettable it is.
 *
 *  Returns the score untouched when there is nothing to say — an unscored
 *  row, or one with no showings left to reason about. */
export function filmRank(score: number | null, shape: RunShape): number | null {
  if (score == null) return null
  if (shape.days === 0) return score
  if (shape.days === 1) return score + oneOffAdjustment(shape.lead)

  const overDays = Math.min(1, (shape.days - 1) / 6)       // a week is a full run
  const overCinemas = Math.min(1, (shape.cinemas - 1) / 3)  // four is everywhere
  return score + 0.10 * (0.6 * overDays + 0.4 * overCinemas)
}

// ---------------------------------------------------------------------------
// Phase 6c: one card per film across every cinema
// ---------------------------------------------------------------------------
//
// Both functions below are pure and dependency-free on purpose, the same
// reason `filmRank`/`runShape` live in this file rather than in entries.ts —
// entries.ts pulls in `./calendar`, `./attribution`, `./exhibitionText`,
// `./classicalForm`, `./reach`, extensionless imports Node's test loader
// cannot resolve, so nothing defined there can be loaded by `node --test`.
// entries.ts re-exports both, the same way it already re-exports `filmRank`
// and `runShape`, so the app still imports everything from one place.

/** Which card a screening belongs on.
 *
 *  `work_id` first — the identity 6a built, joining a screening and a
 *  release of the same film even across recipes and weeks — and the old
 *  title+access key where a row has none, which is every row 6a has not
 *  reached yet. A film with no `work_id` therefore groups exactly as it did
 *  before this phase (rule 2). Access is folded into the fallback key only:
 *  once `work_id` is known, an OV screening and an OmU screening of the same
 *  film are the same film, and the card says which versions play as tags
 *  (`versionTags`) rather than splitting into two cards Doug would have to
 *  notice were the same title. */
export function filmGroupKey(row: { work_id?: string | null; title: string; english_access: string }): string {
  return row.work_id ?? `${row.title.trim().toLowerCase()}|${row.english_access}`
}

/** The subtitle versions on screen, as the house itself prints them.
 *
 *  Distinct `showings.note` values, in a fixed reading order rather than
 *  first-seen — a card listing "OmeU, OV" before "OV, OmeU" depending on
 *  which cinema's showings arrived first would be the same information
 *  looking like two different things between visits. */
const VERSION_ORDER = ['OV', 'OmU', 'OmeU', 'DF'] as const
export type Version = (typeof VERSION_ORDER)[number]

export function versionTags(showings: { note?: string | null }[]): Version[] {
  const seen = new Set(showings.map(s => s.note).filter((v): v is string => !!v))
  return VERSION_ORDER.filter(v => seen.has(v))
}


/** One showing's printed version, or null where it printed none — and null
 *  for any other note, since `note` carries an opening/closing remark on the
 *  rows that are not screenings. */
export function showingVersion(note: string | null | undefined): Version | null {
  const v = note?.trim()
  return v && (VERSION_ORDER as readonly string[]).includes(v) ? (v as Version) : null
}

/** How the sheet names a version in a sentence. The dub is the one a reader
 *  may not know the abbreviation for, and the one that matters most to say
 *  plainly; the three originals are what every Berlin listing prints. */
const VERSION_WORDS: Record<Version, string> = {
  OV: 'OV', OmU: 'OmU', OmeU: 'OmeU', DF: 'German dub',
}

/**
 * The sheet's Language row, from the showings rather than the film alone —
 * films-sunday WP B.
 *
 * `base` is today's wording, the run's verdict (`In English`, `Subtitles`, …)
 * and it is what comes back untouched when no showing carries a version:
 * a source that never prints one says nothing new here. Where some do, each
 * version is named with its dates — `In English · OmU on 30 Sept, 6 Oct ·
 * German dub otherwise`. "Otherwise" goes to the version on the most dates,
 * and only when every showing carries a version: a night that printed
 * nothing is "not said", and "otherwise" would claim it.
 *
 * Dates are calendar days in `tz` — the row's own zone (C2.1) — en-GB
 * (`30 Sept`), the way the chips above print them, up to three; more is a
 * count (`OmU on 5 dates`). Inlined rather than imported, for this file's
 * reason.
 */
export function languageLine(
  base: string,
  showings: { starts_at: string; note?: string | null; kind?: string | null }[],
  tz: string,
): string {
  const nights = showings.filter(s => s.kind !== 'run')
  const dates = new Map<Version, string[]>()
  let unlabelled = 0
  for (const s of [...nights].sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))) {
    const v = showingVersion(s.note)
    if (!v) { unlabelled++; continue }
    const day = new Date(s.starts_at).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', timeZone: tz,
    })
    const list = dates.get(v) ?? []
    if (!list.includes(day)) list.push(day)
    dates.set(v, list)
  }
  const versions = VERSION_ORDER.filter(v => dates.has(v))
  if (!versions.length) return base

  // One version on every night: just its name, no dates to list.
  if (versions.length === 1 && !unlabelled) return [base, VERSION_WORDS[versions[0]]].filter(Boolean).join(' · ')

  // The version on the most dates reads as the rule, the rest as its
  // exceptions; a tie goes to the later one in the reading order, so the dub
  // is the "otherwise" before an original is.
  const rest = unlabelled ? null
    : versions.reduce((a, b) => (dates.get(b)!.length >= dates.get(a)!.length ? b : a))
  // Up to three dates by name; past that a count, since the chips above
  // already carry each night's version and the row is the summary.
  const on = (v: Version) => {
    const d = dates.get(v)!
    return d.length <= 3 ? d.join(', ') : `${d.length} dates`
  }
  const parts = versions
    .filter(v => v !== rest)
    .map(v => `${VERSION_WORDS[v]} on ${on(v)}`)
  if (rest) parts.push(`${VERSION_WORDS[rest]} otherwise`)
  // An empty `base` is a run whose language nobody knows (a1-31): the
  // versions the nights print are then the whole fact.
  return [base, ...parts].filter(Boolean).join(' · ')
}

/** Do this card's nights differ in version? Only then does each date chip
 *  name its own: a week that is OmU on every night says so once, in the
 *  Language row, and its chips stay the width they were. `Wed 30 Sept ·
 *  14:50 · OmU` is one chip a row at 390px where `Wed 30 Sept · 14:50` is
 *  two — the first render of this package showed it adding two rows (~100px)
 *  to a six-date sheet whose every clocked night was OmU.
 *
 *  Only printed versions count. A night that printed nothing is "not said",
 *  not a version of its own: set beside OmU nights it does not make the chips
 *  worth widening (that render's sixth date was a timeless premiere row), and
 *  the Language row still names which dates are OmU. */
export function versionsDiffer(showings: { note?: string | null; kind?: string | null }[]): boolean {
  const seen = new Set(showings.filter(s => s.kind !== 'run').map(s => showingVersion(s.note)).filter(Boolean))
  return seen.size > 1
}
